// AskThread.tsx — the Ask chat's MESSAGE COLUMN + composer, moved VERBATIM out of the former AskView.tsx
// so the peer /ask surface (AskSurface.tsx) can re-house it in a full-page shell without a rewrite.
//
// STYLING NOTE: this file is the app's ONLY Tailwind surface (scoped via src/styles/tailwind.css →
// `@source`). If you move this file you MUST update that `@source` line or the whole chat renders unstyled
// with a green build, green typecheck, and no console error. Colors come from the design tokens
// (bg-card2, text-ink, border-hair, text-acc…), so it still flips with the [data-theme] toggle. Because
// Tailwind's preflight is off, bordered elements carry `border-solid`, native controls `appearance-none`,
// and markdown block elements (which the app's unlayered `*{margin:0;padding:0}` reset would flatten) carry
// explicit spacing/list utilities below. Do not spread Tailwind beyond this file — the shell (AskSurface)
// and launcher are hand-written token CSS.
import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { visit } from 'unist-util-visit';
import type { Root, Text, PhrasingContent } from 'mdast';
import type { QuerySource } from '@terum/contracts';
import type { UseAskQuery } from '../../hooks/useAskQuery';
import { SourceIcon } from '../shared/SourceIcon';
import { siteLabel } from '../../lib/sites';
import { formatSourceDate } from '../../lib/formatting';
import { splitStreamingTail } from '../../lib/streaming-md';

// Shared control chrome — kept as consts so the composer's send/stop and the answer actions stay in sync.
const SEND_BTN =
  'grid h-7 w-7 place-items-center appearance-none rounded-md border-0 bg-acc text-[#06060e] shadow-[0_0_10px_var(--acc-soft)] cursor-pointer disabled:cursor-default disabled:opacity-30 disabled:shadow-none light:text-white';
const STOP_BTN =
  'grid h-7 w-7 place-items-center appearance-none rounded-md border border-solid border-hair2 bg-card2 text-[11px] text-ink cursor-pointer transition-colors hover:border-acc hover:text-acc';
const ACTION_BTN =
  'inline-flex items-center gap-1 appearance-none rounded-md border-0 bg-transparent px-1.5 py-1 font-mono text-[9px] uppercase tracking-[0.1em] text-faint cursor-pointer transition-colors hover:text-ink disabled:cursor-default disabled:opacity-40 disabled:hover:text-faint';

// The detail route a citation deep-links to. scope is resolved server-side (D0): the caller's own private
// note lives at /my-conversations/:id, everything else at /conversations/:id.
function sourceRoute(s: QuerySource): string {
  return `${s.scope === 'private' ? 'my-conversations' : 'conversations'}/${s.id}`;
}

// remark plugin: turn each [n] token into a <cite> element that the renderer resolves to a citation link.
// It visits mdast TEXT nodes only — code spans/blocks are `inlineCode`/`code` nodes, never text — so a
// [1] inside a code sample is left as literal text and never becomes a link (composes with markdown safely).
function remarkCitations() {
  return (tree: Root) => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (!parent || typeof index !== 'number') return;
      if (!/\[\d+\]/.test(node.value)) return;
      const segments = node.value.split(/(\[\d+\])/g).filter((s) => s.length > 0);
      // The top-of-function regex already guaranteed a citation is present, so segments always holds it —
      // do NOT bail on `segments.length < 2`: a text node whose WHOLE value is the citation (e.g. `[1]`
      // alone, which happens right after inline **bold**/*italic*/`code`/links or alone in a list item)
      // yields exactly one segment, and must still be converted, or the citation renders as dead text.
      if (segments.length === 0) return;
      const replacement = segments.map((seg): PhrasingContent => {
        if (/^\[\d+\]$/.test(seg)) {
          // Unknown mdast type → mdast-util-to-hast's unknown handler honors data.hName/hChildren and emits
          // <cite>[n]</cite>. (data.hName on a `text` node would be ignored, so this must NOT be type 'text'.)
          return { type: 'citation', data: { hName: 'cite', hChildren: [{ type: 'text', value: seg }] } } as unknown as PhrasingContent;
        }
        return { type: 'text', value: seg } as Text;
      });
      (parent.children as PhrasingContent[]).splice(index, 1, ...replacement);
      return index + replacement.length;
    });
  };
}

// The inline [n] superscript. Hovering it (or its matching Sources card) lifts `hoveredN`, which
// cross-highlights both — the "hover to the side" link. Clicking still deep-links to the conversation.
function CitationLink({
  src,
  label,
  onNav,
  hoveredN,
  setHoveredN,
}: {
  src: QuerySource;
  label: string;
  onNav: (id: string) => void;
  hoveredN: number | null;
  setHoveredN: (n: number | null) => void;
}) {
  const active = hoveredN === src.n;
  return (
    <sup
      className={`cursor-pointer rounded-[3px] px-px font-semibold text-acc hover:underline focus-visible:outline-2 focus-visible:outline-acc ${active ? 'bg-[var(--acc-soft)] underline' : ''}`}
      role="link"
      tabIndex={0}
      title={src.title ?? src.site}
      onClick={() => onNav(sourceRoute(src))}
      onMouseEnter={() => setHoveredN(src.n)}
      onMouseLeave={() => setHoveredN(null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNav(sourceRoute(src));
        }
      }}
    >
      {label}
    </sup>
  );
}

function childText(children: ReactNode): string {
  return Array.isArray(children) ? children.map((c) => (typeof c === 'string' ? c : '')).join('') : String(children ?? '');
}

// The streaming caret, as CSS. It rides the LAST BLOCK's inline flow via ::after — but the ul/ol
// overrides below are FLEX containers, and a container-level ::after on a flex container is laid out as
// its own flex item (CSS Flexbox §4): on a streamed list the caret dropped onto its own indented row
// below the last bullet. Lists are the dominant Ask answer shape, so for them the caret is suppressed at
// the container and re-attached inside the last <li>, where it trails the text being typed. Other block
// containers (table wrapper, pre, blockquote) keep the container-level caret — a caret on the line after
// the block is acceptable there and those shapes are rarely the trailing block mid-stream.
const CARET_CLASSES =
  " [&>*:last-child]:after:content-['▍']" +
  " [&>ul:last-child]:after:content-none [&>ol:last-child]:after:content-none" +
  " [&>ul:last-child>li:last-child]:after:content-['▍'] [&>ol:last-child>li:last-child]:after:content-['▍']";

// Render the answer as markdown, resolving each [n] through QuerySource.n (NOT the array index — bug-425).
// An [n] with no matching source renders as PLAIN TEXT, never a broken link. Block elements carry explicit
// spacing/list utilities because the app's unlayered `*{margin:0;padding:0}` reset would otherwise flatten
// them (no Tailwind preflight here). Rebuilt per render so a late `sources` frame re-resolves live citations.
//
// `caret` (streaming only, S4-0): the block cursor is a ::after pseudo-element on the LAST RENDERED BLOCK,
// never a character appended into the raw markdown — an in-source caret becomes part of the parse and ends
// up glued inside a table cell or code fence. The pseudo-element rides the last block's inline flow, so it
// trails the last token on the same line exactly as the old in-source caret did.
function MarkdownAnswer({
  answer,
  sources,
  onNav,
  hoveredN,
  setHoveredN,
  caret = false,
}: {
  answer: string;
  sources: QuerySource[];
  onNav: (id: string) => void;
  hoveredN: number | null;
  setHoveredN: (n: number | null) => void;
  caret?: boolean;
}) {
  const byN = new Map(sources.map((s) => [s.n, s]));
  const components: Components = {
    cite: ({ children }) => {
      const label = childText(children);
      const m = label.match(/\d+/);
      const src = m ? byN.get(Number(m[0])) : undefined;
      return src ? (
        <CitationLink src={src} label={label} onNav={onNav} hoveredN={hoveredN} setHoveredN={setHoveredN} />
      ) : (
        <>{label}</>
      );
    },
    p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
    ul: ({ children }) => <ul className="my-2 flex list-disc flex-col gap-1 pl-5 marker:text-faint">{children}</ul>,
    ol: ({ children }) => <ol className="my-2 flex list-decimal flex-col gap-1 pl-5 marker:text-faint">{children}</ol>,
    li: ({ children, id }) => <li id={id} className="pl-0.5 leading-[1.6]">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    // `id` is threaded through: the footnote SECTION's targets live on the <li> (user-content-fn-N) and
    // the backref target on this <a> (user-content-fnref-N) — dropping them would leave the guard below
    // scrolling to nothing.
    a: ({ children, href, id }) => {
      // In-page anchors (GFM footnote refs/backrefs — href="#user-content-fn-1"): the app is a HashRouter,
      // so letting the browser follow one would REWRITE location.hash and route the whole SPA to the `*`
      // catch-all. Scroll to the target instead; never target="_blank" a same-page jump.
      if (href?.startsWith('#')) {
        return (
          <a
            href={href}
            id={id}
            className="text-acc underline"
            onClick={(e) => {
              // preventDefault FIRST — even a decode failure below must never reach the router.
              e.preventDefault();
              let target = href.slice(1);
              try {
                target = decodeURIComponent(target);
              } catch {
                // Malformed %-sequence in a model-written anchor: fall back to the raw fragment.
              }
              document.getElementById(target)?.scrollIntoView({ block: 'nearest' });
            }}
          >
            {children}
          </a>
        );
      }
      return (
        <a href={href} id={id} target="_blank" rel="noreferrer" className="text-acc underline">
          {children}
        </a>
      );
    },
    h1: ({ children }) => <h2 className="mb-1.5 mt-3 font-display text-[15px] font-semibold text-ink first:mt-0">{children}</h2>,
    h2: ({ children }) => <h2 className="mb-1.5 mt-3 font-display text-[15px] font-semibold text-ink first:mt-0">{children}</h2>,
    h3: ({ children }) => <h3 className="mb-1 mt-2.5 font-display text-[13px] font-semibold text-ink first:mt-0">{children}</h3>,
    blockquote: ({ children }) => <blockquote className="my-2 border-l-2 border-solid border-hair2 pl-3 text-ink-s">{children}</blockquote>,
    hr: () => <hr className="my-3 border-0 border-t border-solid border-hair" />,
    code: ({ children, className }) => {
      const isBlock = /\n/.test(childText(children)) || (className?.includes('language-') ?? false);
      return isBlock ? (
        <code className="font-mono text-[12px] leading-relaxed text-ink">{children}</code>
      ) : (
        <code className="rounded bg-card2 px-1 py-0.5 font-mono text-[12px] text-ink">{children}</code>
      );
    },
    pre: ({ children }) => <pre className="my-2 overflow-x-auto rounded-md border border-solid border-hair bg-card2 p-3">{children}</pre>,
    table: ({ children }) => (
      <div className="my-2 overflow-x-auto">
        <table className="w-full border-collapse text-[12px]">{children}</table>
      </div>
    ),
    th: ({ children }) => <th className="border border-solid border-hair px-2 py-1 text-left font-semibold text-ink">{children}</th>,
    td: ({ children }) => <td className="border border-solid border-hair px-2 py-1 text-ink">{children}</td>,
    // Completing the GFM map (S4-0): everything remark-gfm can emit renders deliberately under the app's
    // `*{margin:0;padding:0}` reset + no preflight. (tbody needs no override — the reset takes nothing
    // from it that matters.)
    thead: ({ children }) => <thead className="bg-card2">{children}</thead>,
    tr: ({ children }) => <tr className="align-top">{children}</tr>,
    del: ({ children }) => <del className="text-faint line-through">{children}</del>,
    // Task-list checkboxes (`- [x]`). remark-gfm ships them disabled; readOnly keeps React quiet about a
    // checked box with no onChange. Native appearance — appearance-none would erase the box entirely.
    input: ({ node: _node, className: _cn, ...props }) =>
      props.type === 'checkbox' ? <input {...props} readOnly className="mr-1.5 accent-acc align-middle" /> : <input {...props} readOnly />,
    // Footnote refs arrive as sup>a; the `a` override above keeps them off the router.
    sup: ({ children }) => <sup className="text-[11px]">{children}</sup>,
    section: ({ node: _node, children, ...props }) =>
      'data-footnotes' in props ? (
        <section data-footnotes="" className="mt-3 border-t border-solid border-hair pt-2 text-[12px] text-ink-s">
          {children}
        </section>
      ) : (
        <section {...props}>{children}</section>
      ),
  };
  return (
    <div
      className={`break-words font-sans text-sm leading-[1.65] text-ink${caret ? CARET_CLASSES : ''}`}
    >
      <Markdown remarkPlugins={[remarkGfm, remarkCitations]} components={components}>
        {answer}
      </Markdown>
      {/* When the whole tail is held back (an answer that OPENS with a table), markdown renders no blocks
          and the ::after has nothing to ride — this empty sibling is the caret's landing pad. */}
      {caret && answer.trim() === '' && <span aria-hidden="true" data-caret-anchor />}
    </div>
  );
}

// Pre-first-token indicator: a quick answer's first token is ~20-45s out, and the streaming caret only
// appears once tokens flow — so show something explicit while the answer is still empty.
//
// `label` is the live engine progress (useAskQuery's progressLabel): which sub-query is running, then how
// many notes went to synthesis. When no label is present the render is BYTE-IDENTICAL to before, which is
// the degraded state on an older server that sends no progress frames.
//
// `skills` (skills S1, Teddy directive 2026-08-12): the skills the engine activated this turn,
// accumulated by useAskQuery — annotated on the line for its whole lifetime so a skill functioning is
// visible, not a one-frame flash. The annotation does NOT truncate (skill names are short and bounded;
// the model-written label is the part that can wrap).
function ThinkingIndicator({ label, skills }: { label?: string | null; skills?: string[] | null }) {
  return (
    <div
      className={`flex min-w-0 items-center gap-2 py-0.5 font-mono text-[10px] tracking-[0.16em] text-faint${label ? '' : ' uppercase'}`}
      aria-live="polite"
    >
      <span className="flex shrink-0 gap-1" aria-hidden="true">
        <span className="h-1 w-1 rounded-full bg-faint [animation:ask-thinking_1.4s_ease-in-out_infinite]" />
        <span className="h-1 w-1 rounded-full bg-faint [animation:ask-thinking_1.4s_ease-in-out_infinite] [animation-delay:180ms]" />
        <span className="h-1 w-1 rounded-full bg-faint [animation:ask-thinking_1.4s_ease-in-out_infinite] [animation-delay:360ms]" />
      </span>
      {/* truncate: a model-written sub-query is server-bounded but still long enough to wrap the column */}
      <span className="truncate">{label || 'Thinking…'}</span>
      {skills && skills.length > 0 && (
        <span className="shrink-0 text-acc">using {skills.join(', ')}</span>
      )}
    </div>
  );
}

function AnswerActions({
  answer,
  showCopy,
  canRegenerate,
  onRegenerate,
  onShowInMap,
}: {
  answer: string;
  showCopy: boolean;
  canRegenerate: boolean;
  onRegenerate: () => void;
  onShowInMap?: () => void; // only when the turn has cited sources — frames them on the graph
}) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(answer);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard blocked (permissions / insecure context) — no-op; nothing to persist, nothing lost.
    }
  };
  if (!showCopy && !canRegenerate && !onShowInMap) return null;
  return (
    <div className="mt-2 flex items-center gap-1">
      {showCopy && (
        <button className={ACTION_BTN} onClick={copy} aria-label="Copy answer" title="Copy answer">
          {copied ? '✓ Copied' : '⧉ Copy'}
        </button>
      )}
      {canRegenerate && (
        <button className={ACTION_BTN} onClick={onRegenerate} aria-label="Regenerate answer" title="Regenerate answer">
          ↻ Regenerate
        </button>
      )}
      {onShowInMap && (
        <button className={ACTION_BTN} onClick={onShowInMap} aria-label="Show sources in the map" title="Show these sources on the knowledge map">
          ◉ Show in map
        </button>
      )}
    </div>
  );
}

// One source in the rail: a card with the source system's icon + label, the note's content date, the note
// title (topic-fallback, resolved server-side), and a snippet of its summary. Cross-highlights with its
// inline [n] via `active`.
//
// The DATE is the point of the eyebrow row, not decoration: without it a months-old note reads exactly like
// this morning's, which is how Ask came to report long-merged PRs as open. It is absent on turns persisted
// before the field shipped, and on rows with no capture time — render the row unchanged in that case rather
// than a placeholder, so a missing date never masquerades as a known one.
function SourceCard({
  s,
  onNav,
  active,
  setHoveredN,
}: {
  s: QuerySource;
  onNav: (id: string) => void;
  active: boolean;
  setHoveredN: (n: number | null) => void;
}) {
  const label = s.title ?? siteLabel(s.site);
  const updated = formatSourceDate(s.updated);
  return (
    <button
      className={`flex w-full appearance-none flex-col gap-1 rounded-lg border border-solid bg-transparent px-2.5 py-2 text-left cursor-pointer transition-colors ${active ? 'border-acc bg-[var(--acc-soft)]' : 'border-transparent hover:bg-card'}`}
      onClick={() => onNav(sourceRoute(s))}
      onMouseEnter={() => setHoveredN(s.n)}
      onMouseLeave={() => setHoveredN(null)}
      // "updated", not "from": this is a last-modified time, so a doc note carries the file's mtime and a
      // GitHub note the PR's last activity. The chip is too small to carry that nuance; the tooltip is.
      title={updated ? `Open ${label} — updated ${updated}` : `Open ${label}`}
    >
      <span className="flex items-center gap-1.5">
        <SourceIcon id={s.site} size={15} />
        <span className="font-mono text-[9px] uppercase tracking-[0.08em] text-faint">{siteLabel(s.site)}</span>
        {updated && (
          <span className="font-mono text-[9px] uppercase tracking-[0.08em] text-faint">· {updated}</span>
        )}
        <span className="ml-auto font-mono text-[10px] text-acc">[{s.n}]</span>
      </span>
      <span className="line-clamp-2 font-sans text-[12px] font-medium leading-snug text-ink">{s.title ?? '(untitled)'}</span>
      {s.snippet && <span className="line-clamp-2 font-sans text-[11px] leading-snug text-ink-s">{s.snippet}</span>}
    </button>
  );
}

// The upper-right "Sources" rail (ChatGPT/Perplexity convention). Collapsible header carries stacked source
// icons + a count; the body is the scrollable card list. On narrow screens the parent grid drops it below
// the answer (it stops being sticky) so it reads as an expandable "Sources" section.
function SourcesPanel({
  sources,
  onNav,
  hoveredN,
  setHoveredN,
  open,
  setOpen,
}: {
  sources: QuerySource[];
  onNav: (id: string) => void;
  hoveredN: number | null;
  setHoveredN: (n: number | null) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
}) {
  const sites = [...new Set(sources.map((s) => s.site))].slice(0, 4);
  return (
    <div className="overflow-hidden rounded-xl border border-solid border-hair bg-card2">
      <button
        className="flex w-full appearance-none items-center gap-2 border-0 bg-transparent px-3 py-2.5 text-left cursor-pointer"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={open ? 'Collapse sources' : 'Expand sources'}
      >
        <span className="flex -space-x-1.5">
          {sites.map((site) => (
            <SourceIcon key={site} id={site} size={16} />
          ))}
        </span>
        <span className="font-display text-[13px] font-semibold text-ink">Sources</span>
        <span className="font-mono text-[10px] text-faint">{sources.length}</span>
        <span className={`ml-auto text-[10px] text-faint transition-transform ${open ? '' : 'rotate-180'}`}>▾</span>
      </button>
      {open && (
        // Capped so the whole panel (header + body) stays shorter than the chat viewport — that keeps the
        // sticky header pinned as the answer scrolls; the list scrolls internally past the cap.
        <div className="flex max-h-[min(320px,44vh)] flex-col gap-1 overflow-y-auto border-t border-solid border-hair p-1.5">
          {sources.map((s) => (
            <SourceCard key={s.id} s={s} onNav={onNav} active={hoveredN === s.n} setHoveredN={setHoveredN} />
          ))}
        </div>
      )}
    </div>
  );
}

function AnswerBody({
  turn,
  onNav,
  isLast,
  streaming,
  onRegenerate,
  onShowInMap,
}: {
  turn: UseAskQuery['turns'][number];
  onNav: (id: string) => void;
  isLast: boolean;
  streaming: boolean;
  onRegenerate: () => void;
  onShowInMap: (ids: string[]) => void;
}) {
  const [hoveredN, setHoveredN] = useState<number | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(true);

  if (turn.status === 'error') {
    return (
      <>
        <div className="font-sans text-[13px] leading-snug text-rose">{turn.error || 'Something went wrong.'}</div>
        {isLast && <AnswerActions answer="" showCopy={false} canRegenerate={!streaming} onRegenerate={onRegenerate} />}
      </>
    );
  }
  const thinking = turn.status === 'streaming' && turn.answer === '';
  // A `done` turn with no answer AND no sources can only be a user Stop before the first token arrived —
  // the engine never returns an empty answer (bug-424). Render a neutral "Stopped", never a blank bubble.
  const stopped = turn.status === 'done' && turn.answer === '' && turn.sources.length === 0;
  const hasSources = turn.sources.length > 0;
  const showInMap = hasSources ? () => onShowInMap(turn.sources.map((s) => s.id)) : undefined;

  const answer = (
    <div className="min-w-0">
      {thinking ? (
        <ThinkingIndicator label={turn.progress} skills={turn.skills} />
      ) : stopped ? (
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-faint">Stopped</div>
      ) : (
        // While streaming, parse only the STABLE prefix of the buffer — a trailing fragment inside an
        // unclosed block (a table before its delimiter row, a half-typed closing fence, a bare list
        // marker) is held out of the parse until the block closes, so a half-emitted table never paints
        // as broken pipes (S4-0; spec §4.4). The caret is a ::after on the last block, not a character
        // in the source. A finished turn always renders the full buffer — the hold is streaming-only.
        <MarkdownAnswer
          answer={turn.status === 'streaming' ? splitStreamingTail(turn.answer).stable : turn.answer}
          sources={turn.sources}
          onNav={onNav}
          hoveredN={hoveredN}
          setHoveredN={setHoveredN}
          caret={turn.status === 'streaming'}
        />
      )}
      {turn.status !== 'streaming' && (
        <AnswerActions
          answer={turn.answer}
          showCopy={!!turn.answer}
          canRegenerate={isLast && !streaming}
          onRegenerate={onRegenerate}
          onShowInMap={showInMap}
        />
      )}
    </div>
  );

  // Still streaming (or a Stop) → no sources yet → single column. Once the sources frame lands, split into
  // the answer + a sticky upper-right Sources rail; on narrow screens the parent grid drops the rail below
  // the answer (it stops being sticky) at Tailwind's lg (1024px), so it reads as an expandable section.
  if (!hasSources) return answer;
  return (
    <div className="grid gap-x-5 gap-y-3 lg:grid-cols-[minmax(0,1fr)_248px]">
      {answer}
      <div className="self-start lg:sticky lg:top-1">
        <SourcesPanel
          sources={turn.sources}
          onNav={onNav}
          hoveredN={hoveredN}
          setHoveredN={setHoveredN}
          open={sourcesOpen}
          setOpen={setSourcesOpen}
        />
      </div>
    </div>
  );
}

// The message list — one row per turn (question bubble right, answer left/full-width). Moved verbatim from
// AskView; the only addition is `onShowInMap`, which frames a turn's cited sources on the knowledge graph.
// The scroll container + scroll-pinning live in the parent shell (AskSurface), which owns the grid.
export function AskThread({
  turns,
  onNav,
  streaming,
  onRegenerate,
  onShowInMap,
}: {
  turns: UseAskQuery['turns'];
  onNav: (id: string) => void;
  streaming: boolean;
  onRegenerate: () => void;
  onShowInMap: (ids: string[]) => void;
}) {
  return (
    <>
      {turns.map((turn, i) => (
        // Widen a turn only once it has a Sources rail, so the answer keeps a comfortable reading width
        // (~740px) beside the 248px rail; source-less turns stay at the narrower single-column width.
        <div
          key={i}
          className={`mx-auto flex w-full flex-col gap-3 ${turn.sources.length > 0 ? 'max-w-[1008px]' : 'max-w-[760px]'}`}
        >
          {/* Question right in a bubble; answer left + full-width, no avatar/label. */}
          <div className="flex justify-end">
            <div className="max-w-[82%] whitespace-pre-wrap break-words rounded-2xl border border-solid border-hair bg-card2 px-3.5 py-2 font-sans text-[13px] leading-snug text-ink">
              {turn.question}
            </div>
          </div>
          <div className="flex justify-start">
            <div className="w-full">
              <AnswerBody
                turn={turn}
                onNav={onNav}
                isLast={i === turns.length - 1}
                streaming={streaming}
                onRegenerate={onRegenerate}
                onShowInMap={onShowInMap}
              />
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

// The paperclip button's chrome — sized to match STOP/SEND so the composer row reads as one control set.
const ATTACH_BTN =
  'grid h-7 w-7 shrink-0 place-items-center appearance-none rounded-md border border-solid border-hair2 bg-card2 text-ink cursor-pointer transition-colors hover:border-acc hover:text-acc disabled:cursor-default disabled:opacity-30';

// The accepted extensions, in the register the route publishes (lib/uploads/limits.ts). Passed to the
// file input's `accept` so the OS picker pre-filters; the server re-checks and is authoritative.
const ACCEPT_ATTR = '.pdf,.docx,.xlsx,.pptx,.csv,.tsv,.md,.markdown,.txt,.json,.xml,.html,.htm';

// An inline paperclip — currentColor, so it follows the token theme without a new icon dependency (the
// island otherwise uses ASCII glyphs; a clip has no clean ASCII form). Decorative: the button is labelled.
function PaperclipGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

// One attachment chip: filename + a status the user can read. `indexing` states pulse subtly; a
// failed/rejected chip carries its honest reason (uploadFailureText). Every chip carries a dismiss ✕:
// failed/rejected so the user can clear what they've read, and WORKING chips (bug-721) as the escape
// hatch that lifts the send gate if a file is stuck — dismissing unattaches the chip client-side only.
function UploadChip({ item, onDismiss }: { item: AttachmentChip; onDismiss: (id: string) => void }) {
  const terminalBad = item.status === 'failed' || item.status === 'rejected';
  const working = item.status === 'uploading' || item.status === 'queued' || item.status === 'extracting' || item.status === 'distilling';
  const label =
    item.status === 'ready'
      ? 'ready'
      : terminalBad
        ? item.failureText
        : item.status === 'uploading'
          ? 'uploading…'
          : 'indexing…';
  return (
    <span
      className={
        'inline-flex max-w-[220px] items-center gap-1.5 rounded-md border border-solid px-2 py-1 font-sans text-[11px] ' +
        (terminalBad ? 'border-rose/40 text-rose' : 'border-hair2 text-ink')
      }
      title={`${item.filename} — ${label}`}
    >
      <span className={'shrink-0 ' + (working ? 'animate-pulse text-faint' : terminalBad ? 'text-rose' : 'text-acc')} aria-hidden="true">
        {item.status === 'ready' ? '●' : terminalBad ? '×' : '○'}
      </span>
      <span className="truncate">{item.filename}</span>
      <span className="shrink-0 text-faint">· {label}</span>
      {(terminalBad || working) && (
        <button
          className="ml-0.5 shrink-0 appearance-none border-0 bg-transparent p-0 text-faint cursor-pointer hover:text-ink"
          onClick={() => onDismiss(item.uploadId)}
          aria-label={working ? `Remove ${item.filename}` : `Dismiss ${item.filename}`}
          title={working ? 'Remove this file' : undefined}
        >
          ✕
        </button>
      )}
    </span>
  );
}

// The composer's view of one upload — filename + status + pre-resolved failure text (the hook owns the
// reason→text mapping so this file needs no knowledge of the typed reasons).
export interface AttachmentChip {
  uploadId: string;
  filename: string;
  status: 'uploading' | 'queued' | 'extracting' | 'distilling' | 'ready' | 'failed' | 'rejected';
  failureText: string;
}

// The docked composer: an auto-growing textarea (Enter=send, Shift+Enter=newline, IME-guarded) with an
// attach button, a send/stop button, and (when files are attached) a chip row + honest status line. Its
// POSITION is owned by the shell (ask.css grid); its internals stay Tailwind.
export function AskComposer({
  value,
  onChange,
  onSubmit,
  streaming,
  onStop,
  inputRef,
  placeholder = "Ask anything about your team's work…",
  disabled = false,
  // Upload lane (D32-lite). All optional so a call site without attachments (e.g. a not-found stub)
  // renders the classic composer; a real thread passes them all.
  onAttach,
  attachments = [],
  onDismissAttachment,
  indexing = false,
  attachError = null,
  attachDisabled = false,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  streaming: boolean;
  onStop: () => void;
  inputRef?: RefObject<HTMLTextAreaElement>;
  placeholder?: string;
  disabled?: boolean;
  onAttach?: (files: File[]) => void;
  attachments?: AttachmentChip[];
  onDismissAttachment?: (id: string) => void;
  indexing?: boolean;
  attachError?: string | null;
  attachDisabled?: boolean;
}) {
  const localRef = useRef<HTMLTextAreaElement | null>(null);
  const ref = inputRef ?? localRef;
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Auto-grow to fit content, capped so it never swallows the thread.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  }, [value, ref]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter inserts a newline. Ignore Enter while an IME composition is active.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSubmit();
    }
  };

  const pickFiles = () => fileRef.current?.click();
  const onFilesChosen = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length && onAttach) onAttach(files);
    // Reset so choosing the SAME file again re-fires change (browsers suppress a no-change selection).
    e.target.value = '';
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-col gap-2 rounded-2xl border border-solid border-hair2 bg-card px-3 py-2 focus-within:border-acc focus-within:shadow-[0_0_16px_var(--acc-soft)]">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((a) => (
              <UploadChip key={a.uploadId} item={a} onDismiss={onDismissAttachment ?? (() => {})} />
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          {onAttach && (
            <>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                className="hidden"
                onChange={onFilesChosen}
                aria-hidden="true"
                tabIndex={-1}
              />
              <button
                className={ATTACH_BTN}
                onClick={pickFiles}
                disabled={attachDisabled}
                aria-label="Attach a file"
                title="Attach a file"
                type="button"
              >
                <PaperclipGlyph />
              </button>
            </>
          )}
          <textarea
            ref={ref}
            className="max-h-[168px] w-full resize-none appearance-none border-0 bg-transparent py-0.5 font-sans text-[13px] leading-[1.5] text-ink outline-none placeholder:text-faint disabled:cursor-not-allowed disabled:opacity-60"
            value={value}
            rows={1}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            aria-label="Ask a question"
          />
          {streaming ? (
            <button className={STOP_BTN} onClick={onStop} aria-label="Stop generating" title="Stop generating">
              ■
            </button>
          ) : (
            <button
              className={SEND_BTN}
              onClick={onSubmit}
              disabled={disabled || indexing || !value.trim()}
              aria-label="Send"
              title={indexing ? 'Sending unlocks when your file is ready' : undefined}
            >
              ↑
            </button>
          )}
        </div>
      </div>
      {/* Status line: a local refusal, else the SEND GATE notice (bug-721, supersedes the U9 §5.5
          disclose-don't-block posture): while any attachment is uploading/indexing the send button above
          is disabled and AskSurface.submit() refuses the Enter path, so the line states affirmatively
          what unlocks it. The chip's ✕ is the escape hatch for a stuck file. */}
      {attachError ? (
        <div className="px-1 font-sans text-[11px] text-rose">{attachError}</div>
      ) : indexing ? (
        <div className="px-1 font-sans text-[11px] text-faint" aria-live="polite">
          Getting your file ready. You can send when it's done.
        </div>
      ) : null}
    </div>
  );
}
